<template>
  <v-dialog
    v-model="dialog"
    width="1280"
    persistent>

    <v-card class="dialog-card">

      <!-- Header de Diálogo -->
      <v-card-title class="dialog-header px-6 py-5 d-flex align-center justify-space-between border-bottom">
        <div class="d-flex align-center">
          <div class="filter-header-icon mr-3">
            <v-icon color="primary" size="22">mdi-file-document-edit-outline</v-icon>
          </div>
          <div>
            <div class="text-h6 font-weight-bold slate-900--text" style="line-height: 1.2;">Solicitud de Nivelación</div>
          </div>
        </div>
        <v-btn icon color="grey darken-1" :disabled="guardando" @click="cerrar" small class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <div class="dialog-body pa-6">

        <!-- Info del estudiante / módulo (Pills) -->
        <div v-if="contexto" class="mb-4 w-100">
          <div class="d-flex flex-wrap align-center gap-2 mb-2">
            <div class="filter-pill">
              <v-icon small class="mr-1 primary--text">mdi-account-outline</v-icon>
              <span class="pill-label">Estudiante:</span>
              <span class="pill-value">{{ contexto.estudianteNombre }}</span>
              <span class="persona-ci text-caption ml-2">CI: {{ contexto.estudianteCi }}</span>
            </div>
            <div class="filter-pill">
              <v-icon small class="mr-1 primary--text">mdi-marker</v-icon>
              <span class="pill-label">Nota actual:</span>
              <span class="pill-value red--text text--darken-3 font-weight-bold">{{ contexto.notaActual ?? 0 }}</span>
            </div>
            <div v-if="contexto.notaRecuperatorio !== null && contexto.notaRecuperatorio !== undefined" class="filter-pill">
              <v-icon small class="mr-1 primary--text">mdi-restore</v-icon>
              <span class="pill-label">Recuperatorio:</span>
              <span class="pill-value">{{ contexto.notaRecuperatorio }}</span>
            </div>
            <div v-if="contexto.notaNivelacion !== null && contexto.notaNivelacion !== undefined" class="filter-pill">
              <v-icon small class="mr-1 primary--text">mdi-chevron-double-up</v-icon>
              <span class="pill-label">Nivelación:</span>
              <span class="pill-value">{{ contexto.notaNivelacion }}</span>
            </div>
          </div>
          <div class="d-flex flex-wrap align-center gap-2">
            <div class="filter-pill">
              <v-icon small class="mr-1 primary--text">mdi-bookshelf</v-icon>
              <span class="pill-label">Módulo:</span>
              <span class="pill-value">MOD({{ contexto.moduloNumero }}) {{ contexto.moduloNombre }}</span>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="cargando" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate color="primary" size="36" width="3" />
        </div>

        <template v-else-if="vista === 'pago'">
          <!-- Paso 1: Seleccionar Pago -->
          <div class="section-label mb-3 d-flex align-center">
            <v-btn icon x-small class="mr-2" @click="vista = 'listado'">
              <v-icon small>mdi-arrow-left</v-icon>
            </v-btn>
            <v-icon small color="primary" class="mr-1">mdi-cash-check</v-icon>
            Seleccionar pago de inscripción
          </div>
          <dialog-pagos-inscripcion
            ref="pagosInscripcion"
            :inscripcion-modulo-id="contexto && contexto.inscripcionModuloId"
            @seleccionado="generarSolicitud" />
        </template>

        <template v-else-if="vista === 'formulario'">
          <!-- Paso 2: Formulario de Solicitud -->
          <div class="section-label mb-3 d-flex align-center">
            <v-btn icon x-small class="mr-2" @click="vista = 'listado'">
              <v-icon small>mdi-arrow-left</v-icon>
            </v-btn>
            <v-icon small color="primary" class="mr-1">mdi-plus-circle-outline</v-icon>
            Nueva solicitud de nivelación
          </div>
          
          <div class="dialog-layout-row">
            <!-- Columna Izquierda (40%): Inputs -->
            <div class="dialog-col-left">
              <v-row dense class="mb-2">
                <v-col cols="12" class="mb-2">
                  <span class="input-label">Nota de nivelación</span>
                  <v-text-field
                    v-model.number="nuevaNota"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Ingrese nota (0-100)..."
                    :rules="[(v) => v === null || v === '' || v <= 100 || 'La nota máxima es 100']"
                    dense
                    outlined
                    hide-details
                    class="rounded-lg custom-input mt-1" />
                </v-col>
                
                <v-col cols="12" class="mb-2">
                  <span class="input-label">Documento de respaldo (PDF)</span>
                  <v-file-input
                    v-model="nuevoDocumento"
                    accept="application/pdf"
                    placeholder="Seleccione documento de respaldo..."
                    prepend-icon=""
                    prepend-inner-icon="mdi-file-pdf-box"
                    :rules="[(v) => !!v || 'El documento es obligatorio']"
                    show-size
                    dense
                    outlined
                    hide-details
                    class="rounded-lg custom-input mt-1"
                    @change="onDocumentoChange" />
                </v-col>
                
                <v-col cols="12" class="mb-2">
                  <span class="input-label">Motivo (opcional)</span>
                  <v-text-field
                    v-model="nuevoMotivo"
                    placeholder="Ingrese el motivo de la solicitud..."
                    dense
                    outlined
                    hide-details
                    class="rounded-lg custom-input mt-1" />
                </v-col>
              </v-row>
            </div>

            <!-- Columna Derecha (60%): Vista previa del documento -->
            <div class="dialog-col-right">
              <v-card v-if="documentoPreviewUrl" class="pdf-preview-card" elevation="0" outlined>
                <div class="pdf-preview-header px-4 py-2">
                  <span class="pdf-preview-title">
                    <v-icon x-small color="primary" class="mr-1">mdi-eye-outline</v-icon>
                    Vista previa del documento
                  </span>
                  <v-btn icon x-small @click="limpiarDocumento">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <iframe :src="documentoPreviewUrl" class="pdf-preview-frame" title="Vista previa PDF" />
              </v-card>
              <div v-else class="pdf-placeholder-box">
                <v-icon size="48" color="grey lighten-1">mdi-file-pdf-box</v-icon>
                <div class="text-subtitle-2 grey--text text--darken-1 mt-2">Ningún documento cargado</div>
                <div class="caption grey--text mt-1">Cargue un archivo PDF para visualizar la vista previa aquí.</div>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="vista === 'listado'">
          <!-- Listado de solicitudes -->
          <div class="section-label mb-3 d-flex align-center justify-space-between">
            <span>
              <v-icon small color="primary" class="mr-1">mdi-format-list-bulleted</v-icon>
              Solicitudes registradas
            </span>
            <v-btn
              v-if="!tieneSolicitudActiva && puedeCrearSolicitud"
              depressed
              color="primary"
              class="rounded-lg action-btn px-4"
              style="height: 32px !important;"
              @click="iniciarSeleccionPago">
              <v-icon small left>mdi-plus</v-icon>
              Crear Solicitud
            </v-btn>
          </div>

          <div v-if="!solicitudes.length" class="empty-box mb-4">
            <v-icon size="40" color="grey lighten-1" class="mb-2">mdi-file-hidden</v-icon>
            <div>No hay solicitudes de nivelación registradas para este módulo.</div>
          </div>

          <div v-else class="table-container solicitudes-wrapper mb-4">
            <table class="solicitudes-table w-100">
              <thead>
                <tr>
                  <th class="th-nota text-center">NOTA</th>
                  <th class="th-estado text-center">ESTADO</th>
                  <th class="th-contadora">CONTADORA</th>
                  <th class="th-solicitante">SOLICITADO POR</th>
                  <th class="th-revisor">REVISADO POR</th>
                  <th class="th-acciones text-center">ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="solicitud in solicitudes">
                  <tr :key="solicitud.id">
                    <td class="td-nota-solicitud text-center font-weight-bold">
                      <span class="solicitud-nota">{{ solicitud.nota }}</span>
                      <small v-if="solicitud.notaAnterior !== null" class="solicitud-nota-anterior">
                        antes {{ solicitud.notaAnterior }}
                      </small>
                    </td>
                    <td class="td-estado text-center">
                      <span :class="['custom-badge', 'badge-xs', getEstadoClass(solicitud.estadoSolicitud)]">
                        {{ estadoLabel(solicitud.estadoSolicitud) }}
                      </span>
                    </td>
                    <td class="td-persona">
                      <span v-if="solicitud.contadora" class="persona-nombre">
                        {{ solicitud.contadora.nombreCompleto }}
                        <small class="persona-ci">CI: {{ solicitud.contadora.ci }}</small>
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td class="td-persona">
                      <span v-if="solicitud.solicitadoPor" class="persona-nombre">
                        {{ solicitud.solicitadoPor.nombreCompleto }}
                        <small class="persona-ci">CI: {{ solicitud.solicitadoPor.ci }}</small>
                      </span>
                      <span v-else class="text-muted">—</span>
                      <small class="persona-fecha">
                        <v-icon x-small color="grey lighten-1" class="mr-0.5">mdi-calendar-clock</v-icon>
                        {{ formatFecha(solicitud.fechaSolicitud) }}
                      </small>
                    </td>
                    <td class="td-persona">
                      <span v-if="solicitud.revisadoPor" class="persona-nombre">
                        {{ solicitud.revisadoPor.nombreCompleto }}
                        <small class="persona-ci">CI: {{ solicitud.revisadoPor.ci }}</small>
                      </span>
                      <span v-else class="no-revisor">Sin revisar</span>
                      <small v-if="solicitud.revisadoPor" class="persona-fecha">
                        <v-icon x-small color="grey lighten-1" class="mr-0.5">mdi-calendar-check</v-icon>
                        {{ formatFecha(solicitud.fechaRevision) }}
                      </small>
                    </td>
                    <td class="td-acciones text-center">
                      <div class="d-flex flex-column gap-1">
                        <v-btn
                          depressed
                          outlined
                          color="primary"
                          class="rounded-lg action-btn w-100 compact-action-btn"
                          @click="verSolicitud(solicitud)">
                          <v-icon x-small left>mdi-eye-outline</v-icon>
                          Ver
                        </v-btn>
                        <v-btn
                          v-if="solicitud.estadoSolicitud === ESTADOS.GENERADO && puedeCargarNota"
                          depressed
                          color="primary"
                          class="rounded-lg action-btn apply-btn w-100 compact-action-btn"
                          @click="continuarSolicitud(solicitud)">
                          <v-icon x-small left>mdi-note-plus-outline</v-icon>
                          Cargar nota
                        </v-btn>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-if="tieneSolicitudActiva" class="warning-alert-box mt-3 d-flex align-center">
            <v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span>Ya existe una solicitud de nivelación en curso. Debe completarse o revisarse antes de crear una nueva.</span>
          </div>
        </template>

      </div>

      <v-card-actions v-if="vista === 'formulario' && !cargando" class="dialog-actions px-6 py-4 bg-light d-flex justify-end gap-2 border-top">
        <v-btn
          depressed
          outlined
          class="rounded-lg action-btn cancel-btn px-6"
          :disabled="guardando"
          @click="vista = 'listado'">
          Cancelar
        </v-btn>
        <v-btn
          v-if="puedeCargarNota"
          depressed
          color="primary"
          class="rounded-lg action-btn apply-btn px-6"
          :loading="guardando"
          :disabled="nuevaNota === null || nuevaNota === '' || nuevaNota > 100 || nuevaNota < 0 || !nuevoDocumento"
          @click="crearSolicitud">
          <v-icon left small>mdi-send</v-icon>
          Enviar solicitud
        </v-btn>
      </v-card-actions>

    </v-card>

    <dialog-revisar-solicitud-nivelacion ref="dialogRevisar" />
  </v-dialog>
</template>

<script>
import SolicitudCalificacionNivelacionService from '../../services/solicitud-calificacion-nivelacion.service';
import DialogPagosInscripcion from './DialogPagosInscripcion.vue';
import DialogRevisarSolicitudNivelacion from '../../nivelaciones/components/DialogRevisarSolicitudNivelacion.vue';
import {
  ESTADOS_SOLICITUD_CALIFICACION_NIVELACION,
  ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_LABELS,
  ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_COLORES,
} from '../../utils/constantes';

export default {
  name: 'DialogSolicitudNivelacion',
  components: { DialogPagosInscripcion, DialogRevisarSolicitudNivelacion },
  data() {
    return {
      dialog: false,
      contexto: null,
      solicitudes: [],
      cargando: false,
      guardando: false,
      nuevaNota: null,
      nuevoMotivo: '',
      nuevoDocumento: null,
      documentoBase64: null,
      documentoPreviewUrl: null,
      vista: 'listado',
      solicitudIdEnEdicion: null,
      ESTADOS: ESTADOS_SOLICITUD_CALIFICACION_NIVELACION,
    };
  },
  computed: {
    tieneSolicitudActiva() {
      return this.solicitudes.some(
        (s) => s.estadoSolicitud === this.ESTADOS.GENERADO || s.estadoSolicitud === this.ESTADOS.PENDIENTE,
      );
    },
    puedeCrearSolicitud() {
      const idCargo = this.$store.state.CARGO?.id;
      return idCargo === 1 || idCargo === 15; // MASTER || AUXILIAR CONTABLE
    },
    puedeCargarNota() {
      const idCargo = this.$store.state.CARGO?.id;
      return idCargo === 1 || idCargo === 7 || idCargo === 6; // MASTER || AUXILIAR ACADEMICO || COORDINADOR ACADEMICO
    },
  },
  methods: {
    abrir(contexto) {
      this.contexto = contexto;
      this.nuevaNota = null;
      this.nuevoMotivo = '';
      this.limpiarDocumento();
      this.vista = 'listado';
      this.solicitudIdEnEdicion = null;
      this.dialog = true;
      this.cargarSolicitudes();
    },
    cerrar() {
      this.dialog = false;
      this.contexto = null;
      this.solicitudes = [];
      this.limpiarDocumento();
    },
    onDocumentoChange(archivo) {
      if (this.documentoPreviewUrl) {
        URL.revokeObjectURL(this.documentoPreviewUrl);
        this.documentoPreviewUrl = null;
      }
      this.documentoBase64 = null;
      if (!archivo) return;

      this.documentoPreviewUrl = URL.createObjectURL(archivo);

      const reader = new FileReader();
      reader.onload = (e) => {
        this.documentoBase64 = e.target.result.split(',')[1];
      };
      reader.readAsDataURL(archivo);
    },
    limpiarDocumento() {
      if (this.documentoPreviewUrl) {
        URL.revokeObjectURL(this.documentoPreviewUrl);
      }
      this.documentoPreviewUrl = null;
      this.nuevoDocumento = null;
      this.documentoBase64 = null;
    },
    estadoLabel(estado) {
      return ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_LABELS[estado] || estado;
    },
    estadoColor(estado) {
      return ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_COLORES[estado] || 'grey';
    },
    getEstadoClass(estado) {
      if (!estado) return 'state-default';
      const norm = estado.toUpperCase().trim();
      if (norm === 'APROBADO' || norm === 'APROBADA') return 'state-concluido';
      if (norm === 'GENERADO' || norm === 'PENDIENTE') return 'state-en-desarrollo';
      if (norm === 'RECHAZADO' || norm === 'RECHAZADA') return 'state-no-iniciado';
      return 'state-default';
    },
    formatFecha(fecha) {
      if (!fecha) return '—';
      return new Date(fecha).toLocaleString('es-BO', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    },
    async cargarSolicitudes() {
      if (!this.contexto?.inscripcionModuloId) return;
      this.cargando = true;
      try {
        const { data } = await SolicitudCalificacionNivelacionService.listarPorInscripcionModulo(
          this.contexto.inscripcionModuloId,
        );
        this.solicitudes = data?.datos || [];
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al obtener las solicitudes';
        this.$notify({ group: 'notifications', title: 'Error', text: mensaje, type: 'error' });
      } finally {
        this.cargando = false;
      }
    },
    iniciarSeleccionPago() {
      this.vista = 'pago';
      this.$nextTick(() => {
        this.$refs.pagosInscripcion?.cargar();
      });
    },
    async generarSolicitud(pago) {
      if (!this.contexto?.inscripcionModuloId) return;

      const confirmed = await this.$dialog.confirm(
        {
          title: 'Confirmar selección de pago',
          body: '¿Está seguro de que desea seleccionar este pago para la solicitud de nivelación?',
        },
        {
          okText: 'Sí',
          cancelText: 'No',
        }
      ).then(() => true).catch(() => false);

      if (!confirmed) {
        this.$refs.pagosInscripcion?.detenerSeleccion();
        return;
      }

      try {
        await SolicitudCalificacionNivelacionService.generar({
          inscripcionModuloId: this.contexto.inscripcionModuloId,
          pagosInscripcionId: pago.id,
        });
        this.$notify({
          group: 'notifications', title: 'Éxito', text: 'Pago de inscripción seleccionado correctamente', type: 'success',
        });
        this.vista = 'listado';
        await this.cargarSolicitudes();
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al seleccionar el pago de inscripción';
        this.$notify({ group: 'notifications', title: 'Error', text: mensaje, type: 'error' });
      } finally {
        this.$refs.pagosInscripcion?.detenerSeleccion();
      }
    },
    verSolicitud(solicitud) {
      this.$refs.dialogRevisar.abrir(solicitud.id, true);
    },
    continuarSolicitud(solicitud) {
      this.solicitudIdEnEdicion = solicitud.id;
      this.nuevaNota = null;
      this.nuevoMotivo = solicitud.motivo || '';
      this.limpiarDocumento();
      this.vista = 'formulario';
    },
    async crearSolicitud() {
      if (!this.contexto?.inscripcionModuloId) return;

      const confirmed = await this.$dialog.confirm(
        {
          title: 'Confirmar envío de solicitud',
          body: `¿Está seguro de que desea enviar la solicitud de nivelación con nota ${this.nuevaNota}?`,
        },
        {
          okText: 'Sí',
          cancelText: 'No',
        }
      ).then(() => true).catch(() => false);

      if (!confirmed) return;

      this.guardando = true;
      try {
        await SolicitudCalificacionNivelacionService.crear({
          inscripcionModuloId: this.contexto.inscripcionModuloId,
          solicitudId: this.solicitudIdEnEdicion || undefined,
          nota: this.nuevaNota,
          motivo: this.nuevoMotivo || undefined,
          documentoBase64: this.documentoBase64 || undefined,
        });
        this.$notify({
          group: 'notifications', title: 'Éxito', text: 'Solicitud de nivelación enviada', type: 'success',
        });
        this.nuevaNota = null;
        this.nuevoMotivo = '';
        this.limpiarDocumento();
        this.solicitudIdEnEdicion = null;
        this.vista = 'listado';
        await this.cargarSolicitudes();
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al crear la solicitud';
        this.$notify({ group: 'notifications', title: 'Error', text: mensaje, type: 'error' });
      } finally {
        this.guardando = false;
      }
    },
  },
};
</script>

<style scoped>
.dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background-color: #ffffff !important;
}

.dialog-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.filter-header-icon {
  background: #eff6ff;
  border-radius: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dbeafe;
}

.slate-900--text {
  color: #0f172a !important;
}

.dialog-body {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  background: #ffffff !important;
}

.dialog-actions {
  flex: 0 0 auto;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

/* Pills for student info */
.filter-pill {
  display: inline-flex;
  align-items: center;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.8rem;
  color: #334155;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.filter-pill:hover {
  border-color: #cbd5e1;
  background-color: #f1f5f9;
}

.pill-label {
  font-weight: 600;
  color: #64748b;
  margin-right: 6px;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.03em;
}

.pill-value {
  font-weight: 600;
  color: #1e293b;
}

.persona-ci {
  color: #64748b;
  font-weight: 500;
}

/* Section label styling */
.section-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #2563eb;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
}

.warning-alert-box {
  background-color: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.8rem;
  color: #b45309;
  font-weight: 500;
}

/* Custom Input Fields */
.input-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  display: inline-block;
}

.text-error {
  color: #ef4444 !important;
}

.custom-input ::v-deep .v-input__slot {
  background-color: #f8fafc !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: none !important;
  min-height: 40px !important;
}

.custom-input ::v-deep .v-input__slot:hover {
  background-color: #f1f5f9 !important;
  border-color: #94a3b8 !important;
}

.custom-input ::v-deep .v-input__slot:focus-within {
  background-color: #ffffff !important;
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15) !important;
}

.custom-input ::v-deep fieldset {
  border: none !important;
}

.custom-input ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.custom-input ::v-deep .v-input__append-inner {
  margin-top: 8px !important;
}

.custom-input ::v-deep .v-icon {
  color: #64748b !important;
  font-size: 20px !important;
}

.custom-input ::v-deep input {
  color: #1e293b !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
}

.custom-input ::v-deep textarea {
  color: #1e293b !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
}

/* Buttons style */
.action-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  height: 40px !important;
  border-radius: 8px !important;
}

.compact-action-btn {
  height: 32px !important;
  font-size: 0.75rem !important;
  border-radius: 6px !important;
}

.apply-btn {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2), 0 2px 4px -1px rgba(37, 99, 235, 0.1) !important;
  transition: all 0.2s ease !important;
}

.apply-btn:hover {
  box-shadow: 0 6px 8px -1px rgba(37, 99, 235, 0.25), 0 3px 6px -1px rgba(37, 99, 235, 0.15) !important;
  transform: translateY(-1px);
}

.apply-btn.v-btn--disabled {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  box-shadow: none !important;
  border-color: #e2e8f0 !important;
}

.cancel-btn {
  border: 1px solid #cbd5e1 !important;
  color: #475569 !important;
  background-color: #ffffff !important;
}

.cancel-btn:hover {
  background-color: #f8fafc !important;
  color: #1e293b !important;
}

.close-btn {
  transition: transform 0.2s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
}

/* Empty Box design */
.empty-box {
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 24px 16px;
  text-align: center;
  font-size: 0.85rem;
  color: #64748b;
}

/* ── Table design ── */
.table-container {
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.solicitudes-table {
  border-collapse: collapse;
  font-size: 0.8rem;
}

.solicitudes-table thead tr th {
  background-color: #f8fafc !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  border-bottom: 2px solid #cbd5e1 !important;
  height: 44px !important;
  padding: 8px 12px;
  white-space: nowrap;
  text-align: left;
}

.solicitudes-table tbody tr {
  transition: background-color 0.2s ease;
}

.solicitudes-table tbody tr:nth-child(odd) {
  background-color: #ffffff;
}

.solicitudes-table tbody tr:nth-child(even) {
  background-color: #f8fafc;
}

.solicitudes-table tbody tr:hover {
  background-color: #eff6ff !important;
}

.solicitudes-table tbody tr td {
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 12px;
  vertical-align: middle;
  color: #475569;
}

.th-nota { width: 80px; }
.th-estado { width: 100px; }
.th-solicitante { min-width: 150px; }
.th-acciones { width: 120px; }

.solicitud-nota {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  display: block;
}

.solicitud-nota-anterior {
  display: block;
  font-size: 0.7rem;
  color: #94a3b8;
  margin-top: 1px;
}

.td-persona {
  min-width: 140px;
}

.persona-nombre {
  display: block;
  font-weight: 600;
  color: #1e293b;
}

.persona-ci {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 500;
}

.persona-fecha {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 0.75rem;
  margin-top: 3px;
  font-weight: 500;
}

.no-revisor {
  color: #94a3b8;
  font-style: italic;
}

.td-rechazo {
  background: #fef2f2 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #fee2e2 !important;
}

/* Badges for status */
.custom-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 9999px;
  line-height: 1.25;
  white-space: nowrap;
}

.badge-xs {
  font-size: 0.65rem;
  padding: 1px 6px;
}

.state-concluido {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid rgba(46, 125, 50, 0.2);
}

.state-en-desarrollo {
  background-color: #fff3e0;
  color: #ef6c00;
  border: 1px solid rgba(239, 108, 0, 0.2);
}

.state-no-iniciado {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid rgba(198, 40, 40, 0.2);
}

.state-default {
  background-color: #eceff1;
  color: #455a64;
  border: 1px solid rgba(69, 90, 100, 0.2);
}

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }

/* 2-Column Row Layout */
.dialog-layout-row {
  display: flex;
  gap: 24px;
  align-items: stretch;
}

.dialog-col-left {
  flex: 4;
  min-width: 320px;
}

.dialog-col-right {
  flex: 6;
  min-width: 480px;
  display: flex;
  flex-direction: column;
}

@media (max-width: 960px) {
  .dialog-layout-row {
    flex-direction: column;
  }
  .dialog-col-left, .dialog-col-right {
    width: 100%;
    min-width: 0;
  }
}

/* PDF Preview Frame */
.pdf-preview-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #cbd5e1 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 550px;
}

.pdf-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.pdf-preview-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #2563eb;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
}

.pdf-preview-frame {
  width: 100%;
  flex: 1 1 auto;
  border: none;
  background-color: #ffffff;
  min-height: 500px;
}

.pdf-placeholder-box {
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 40px 16px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 550px;
}

.bg-light {
  background-color: #f8fafc;
}
</style>
